/**
 * The vendor's approval status, on every screen it matters on.
 *
 * A vendor could sign in, add services, set availability and wait for bookings that were
 * never going to come, because their application had been rejected and the only place that
 * said so was a card on the dashboard. Approval is not dashboard trivia — while it is
 * unresolved it is the single fact that determines whether any of the work does anything.
 *
 * So: shown whenever the status blocks trading, on whatever page the vendor is on, from
 * the moment they log in. Silent once approved — a permanent "you are fine" bar is noise,
 * and the dashboard already carries the green state.
 */
import { AlertTriangle, Clock, ShieldCheck } from 'lucide-react';
import { use_vendor_profile } from '@/features/vendor-profile/hooks';
import { kyc_presentation } from '@/features/vendor-profile/kyc-status';

const TONE_STYLE: Record<string, string> = {
  destructive: 'border-[hsl(var(--destructive)/0.3)] bg-[hsl(var(--destructive)/0.1)] text-destructive',
  warning: 'border-[hsl(var(--warning)/0.3)] bg-[hsl(var(--warning)/0.12)] text-warning',
  info: 'border-[hsl(var(--info)/0.3)] bg-[hsl(var(--info)/0.1)] text-info',
  success: 'border-[hsl(var(--success)/0.3)] bg-[hsl(var(--success)/0.1)] text-success',
  neutral: 'border-border bg-muted text-muted-foreground',
};

export function VendorStatusBanner() {
  const profile_q = use_vendor_profile();

  /*
   * While the profile is loading, and if it fails, show nothing. A banner that flashes
   * "Status unavailable" on every page load would train vendors to ignore the one place
   * a real rejection appears.
   */
  if (!profile_q.data) return null;

  const kyc = kyc_presentation(profile_q.data.kyc_status);
  if (!kyc.blocks_trading) return null;

  const Icon =
    kyc.tone === 'destructive' ? AlertTriangle : kyc.tone === 'info' ? Clock : ShieldCheck;

  return (
    <div
      className={`mb-4 flex items-start gap-3 rounded-card border px-4 py-3 ${TONE_STYLE[kyc.tone] ?? TONE_STYLE.neutral}`}
      data-testid="vendor-status-banner"
      data-kyc-status={profile_q.data.kyc_status}
      role="status"
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0">
        <p className="font-semibold" data-testid="vendor-status-headline">
          {kyc.headline}
        </p>
        <p className="text-sm opacity-90">{kyc.detail}</p>
        {/* Said plainly, because it is the consequence a vendor actually cares about. */}
        <p className="mt-1 text-xs opacity-75">
          Customers cannot book you until your account is approved.
        </p>
      </div>
    </div>
  );
}
